import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const TemplatePreview = ({ template, width = 792, height = 198 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!template || !template.template || !template.template.pages) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 1584;
    canvas.height = 396;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Get template elements
    const templatePage = template.template.pages[0];
    if (!templatePage || !templatePage.elements) return;

    // Set background
    const bgColor = template.template.canvasSettings?.background || '#ffffff';
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Render elements in order
    templatePage.elements.forEach(element => {
      renderElement(ctx, element);
    });

  }, [template]);

  const renderElement = (ctx, element) => {
    ctx.save();

    switch (element.type) {
      case 'rectangle':
        ctx.fillStyle = element.fill || '#000000';
        ctx.fillRect(element.x, element.y, element.width, element.height);
        
        if (element.stroke) {
          ctx.strokeStyle = element.stroke;
          ctx.lineWidth = element.strokeWidth || 1;
          ctx.strokeRect(element.x, element.y, element.width, element.height);
        }
        break;

      case 'circle':
        ctx.beginPath();
        ctx.arc(element.x + (element.radius || 50), element.y + (element.radius || 50), element.radius || 50, 0, 2 * Math.PI);
        ctx.fillStyle = element.fill || '#000000';
        if (element.opacity !== undefined) {
          ctx.globalAlpha = element.opacity;
        }
        ctx.fill();
        
        if (element.stroke) {
          ctx.strokeStyle = element.stroke;
          ctx.lineWidth = element.strokeWidth || 1;
          ctx.stroke();
        }
        break;

      case 'line':
        ctx.beginPath();
        if (element.points) {
          ctx.moveTo(element.x + element.points[0], element.y + element.points[1]);
          ctx.lineTo(element.x + element.points[2], element.y + element.points[3]);
        } else {
          ctx.moveTo(element.x, element.y);
          ctx.lineTo(element.x + (element.width || 100), element.y);
        }
        ctx.strokeStyle = element.stroke || '#000000';
        ctx.lineWidth = element.strokeWidth || 1;
        ctx.stroke();
        break;

      case 'heading':
      case 'paragraph':
      case 'text':
        ctx.fillStyle = element.fill || '#000000';
        ctx.font = `${element.fontWeight || 'normal'} ${element.fontSize || 20}px ${element.fontFamily || 'Arial'}`;
        
        // Handle text alignment
        let textX = element.x;
        if (element.align === 'center') {
          ctx.textAlign = 'center';
          // For center alignment, use the center point
          textX = element.x + (element.width || 0) / 2;
        } else if (element.align === 'right') {
          ctx.textAlign = 'right';
          textX = element.x + (element.width || 0);
        } else {
          ctx.textAlign = 'left';
          textX = element.x;
        }
        
        ctx.textBaseline = 'top';
        
        // Handle text rendering (simplified to avoid stacking)
        const text = element.text || '';
        const maxWidth = element.width || 1000;
        
        // For templates, just render the text directly without complex wrapping
        if (text.includes('\n')) {
          // Handle explicit line breaks
          const lines = text.split('\n');
          const lineHeight = (element.fontSize || 20) * (element.lineHeight || 1.2);
          lines.forEach((line, index) => {
            ctx.fillText(line, textX, element.y + (index * lineHeight), maxWidth);
          });
        } else {
          // Single line or simple text
          ctx.fillText(text, textX, element.y, maxWidth);
        }
        break;

      default:
        // Handle unknown element types
        console.log('Unknown element type:', element.type);
        break;
    }

    ctx.restore();
  };

  return (
    <Box sx={{
      width: '100%',
      maxWidth: width,
      aspectRatio: '1584/396',
      position: 'relative',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: 'white'
    }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block'
        }}
      />
    </Box>
  );
};

export default TemplatePreview; 